import firebase from "firebase/app";

import { Button, Card, Icon, Message } from "semantic-ui-react";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import createOrUpdateSession from "astrid-firestore/src/api/sessions/createOrUpdateSession";
import notifySessions from "astrid-firestore/src/api/sessions/notifications/notifySessions";
import getSessionTimestamps from "astrid-firestore/src/api/sessions/utils/getSessionTimestamps";
import { getSessionOverlapError } from "astrid-firestore/src/api/sessions/utils/sessionOverlap";
import sessionOfferTypes from "astrid-firestore/src/constants/sessionOfferTypes";
import { teamRoles } from "astrid-firestore/src/constants/teamRoles";
import { useDocumentData } from "astrid-firestore/src/hooks";

import Flex from "../../../../components/Flex/Flex";

import { useProfile } from "../../../authentication/state/profile";
import Form from "../../../forms/components/Form/Form";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";
import { formatNamedPrice } from "../../../prices/utils/priceFormatters";
import ProductionInstantSearchSelect from "../../../productions/components/ProductionInstantSearchSelect/ProductionInstantSearchSelect";
import ProductionMemberUserSelect from "../../../productions/components/ProductionMemberUserSelect/ProductionMemberUserSelect";
import useStudioOptions from "../../../studios/hooks/useStudioOptions";

import useSessionErrorTranslations from "../../hooks/useSessionErrorTranslations";
import useSessionOptions from "../../hooks/useSessionOptions";

export default function CalendarEditSessionForm({ sessions, session, defaultProduction, onSuccess }) {
	const { t } = useTranslation();
	const user = useProfile();

	const sessionErrorTranslations = useSessionErrorTranslations();

	const [showPriceDropdown, setShowPriceDropdown] = useState(false);

	const { form, error, watch, setValue, formState, onSubmit } = useSimpleForm({
		defaultValues: {
			productionId: defaultProduction.id,
			date: session.start?.toDate(),
			startTime: session.start?.toDate(),
			endTime: session.end?.toDate(),
			readerId: session.reader?.[0],
			studioId: session.studio,
			recorderId: session.recorder,
			priceId: session.priceId,
			price: session.priceRef || null,
			agreement: session.agreementRef || null,
			polish: session.polish,
			notifyAffectedUsers: true,
		},
		onSubmit: async ({
			productionId,
			readerId,
			polish,
			priceId,
			recorderId,
			price,
			agreement,
			notifyAffectedUsers,
		}) => {
			const studio = selectedStudio.data;

			const after = await createOrUpdateSession(firebase, {
				session,
				price,
				priceId,
				agreement,
				productionId,
				user,
				studio,
				reader: [readerId],
				recorder: recorderId,
				start,
				end,
				polish,
				sessions,
			});

			if (notifyAffectedUsers) {
				const userIdsToNotify = [session.reader[0], session.recorder, after.reader[0], after.recorder]
					.filter(Boolean)
					.filter((id) => id !== user.id);

				await notifySessions({ modifiedSessions: [{ before: session, after }], userIdsToNotify, t });
			}
		},
		onSuccess,
	});

	const { isValid, isSubmitting } = formState;

	const { readerId, recorderId, productionId, date, startTime, endTime, studioId } = watch();

	const [production] = useDocumentData(db.collection("productions").doc(productionId));

	const { start, end } = getSessionTimestamps({ date, startTime, endTime });

	const [studioOptions, loadingStudioOptions] = useStudioOptions({ organizationId: production?.producer });

	const filteredStudioOptions = studioOptions.map((studio) => {
		const overlapError = getSessionOverlapError({
			start,
			end,
			studio,
			reader: [readerId],
			recorder: recorderId,
			production: productionId,
			sessions: sessions.filter((s) => s.id !== session.id),
		});

		return {
			...studio,
			overlapError,
			disabled: !!overlapError,
		};
	});

	const selectedStudio = filteredStudioOptions.find((s) => s.id === studioId);

	const [{ recorderOptions, recorderPriceOptions }] = useSessionOptions({ production, recorderId });

	useEffect(() => {
		if (production && recorderId && !recorderOptions.some(({ value }) => value === recorderId)) {
			setValue("recorderId", sessionOfferTypes.ANY_RECORDER_IN_TEAM);
		}
	}, [production, recorderId, recorderOptions, setValue]);

	useEffect(() => {
		if (production && productionId !== production?.id) {
			setValue("priceId", null);
			setValue("price", null);
			setValue("agreement", null);
			setShowPriceDropdown(true);
		}
	}, [production, production?.id, productionId, setValue]);

	return (
		<Form form={form}>
			<Card fluid style={{ padding: 5 }}>
				<Card.Content>
					<Form.Group widths="equal">
						<Form.DateInput name="date" label={t("date", "Date")} />

						<Form.TimeInput name="startTime" label={t("start", "Start")} />

						<Form.TimeInput name="endTime" label={t("end", "End")} />
					</Form.Group>

					<Form.Group widths="equal">
						<Form.Field name="readerId" rules={{ required: false }}>
							<ProductionMemberUserSelect
								fluid
								label={t("reader", "Narrator")}
								production={production}
								role={teamRoles.READER}
							/>
						</Form.Field>

						<Form.Field name="productionId">
							<ProductionInstantSearchSelect
								fluid
								organizationId={production?.producer}
								readerId={readerId}
							/>
						</Form.Field>

						<Form.Select
							name="studioId"
							label={t("Studio", "Studio")}
							fluid
							options={filteredStudioOptions}
							loading={loadingStudioOptions}
							getMissingOption={() => ""}
						/>
					</Form.Group>

					<Form.Group widths="equal">
						<Form.Select
							name="recorderId"
							label={t("producerRecorder", "Recording engineer")}
							options={recorderOptions}
							disabled={!production}
							onChangeOptions={() => {
								setValue("priceId", null);
								setValue("price", null);
								setValue("agreement", null);

								setShowPriceDropdown(true);
							}}
						/>

						{session.priceRef && showPriceDropdown === false ? (
							<div className="ui field">
								<label>{t("price", "Price")}</label>

								<Flex justifyContent="flex-start" gap={10}>
									<p style={{ marginTop: 12 }}>{formatNamedPrice(session.priceRef)}</p>

									<Icon name="pencil" color="blue" onClick={() => setShowPriceDropdown(true)} />
								</Flex>
							</div>
						) : (
							<Form.Select
								name="priceId"
								label={t("price", "Price")}
								options={recorderPriceOptions}
								rules={{ required: false }}
								onChangeOption={(option) => {
									if (!option) {
										// means no match and the price should be removed.
										setValue("priceId", null);
										setValue("price", null);
										setValue("agreement", null);
									} else if (option.data) {
										setValue("price", option.data.price);
										setValue("agreement", option.data.agreement);
									}
								}}
							/>
						)}
					</Form.Group>

					<Form.Checkbox name="polish" label={t("polishSession")} rules={{ required: false }} />

					{selectedStudio?.overlapError && (
						<Message negative>{sessionErrorTranslations[selectedStudio.overlapError]}</Message>
					)}

					{error && <Message negative>{t("errorMessageHeader", "Something went wrong")}</Message>}
				</Card.Content>
				<Card.Content extra>
					<Flex>
						<div>
							<Form.Checkbox
								name="notifyAffectedUsers"
								label={t("notifyAffectedUsers", "Notify affected users")}
								rules={{ required: false }}
							/>
						</div>

						<Button
							loading={isSubmitting}
							disabled={selectedStudio?.disabled || !isValid || isSubmitting}
							floated="right"
							color="green"
							onClick={onSubmit}
						>
							{t("saveChanges", "Save changes")}
						</Button>
					</Flex>
				</Card.Content>
			</Card>
		</Form>
	);
}
